/**
 * 🚀 QUICK ACCESS GRID - Accès Rapide aux Modules Client Malin
 * Grille d'icônes style macOS intégrée directement dans la page
 * Placé en dessous du QR Code
 */

import React from 'react';
import { motion } from 'framer-motion';
import { 
  CreditCard, MessageSquare, Truck, Gamepad2, Heart, 
  Radio, Cloud, Tv, Newspaper, Calendar, Zap, Trophy,
  Vote, BarChart3, Users, User, Shield, QrCode, Grid3X3
} from 'lucide-react';

// 🎨 Configuration des modules avec design style macOS
const QUICK_ACCESS_MODULES = [
  // Rangée 1 - Modules principaux
  {
    id: 'cards',
    name: 'Mes Cartes',
    icon: CreditCard,
    gradient: 'from-cyan-400 to-blue-600',
    action: 'scrollTo',
    target: 'vault'
  },
  {
    id: 'chat',
    name: 'Parle-nous',
    icon: MessageSquare,
    gradient: 'from-amber-400 to-orange-500',
    action: 'scrollTo',
    target: 'contact'
  },
  {
    id: 'delivery',
    name: 'Livraison',
    icon: Truck,
    gradient: 'from-emerald-400 to-green-600',
    action: 'setShow',
    target: 'showMaxAssistant'
  },
  {
    id: 'games',
    name: 'Jeux',
    icon: Gamepad2,
    gradient: 'from-purple-400 to-violet-600',
    action: 'setShow',
    target: 'showGames'
  },
  {
    id: 'heartbeats',
    name: 'Heartbeats',
    icon: Heart,
    gradient: 'from-pink-400 to-rose-600',
    action: 'setShow',
    target: 'showHeartbeats'
  },
  // Rangée 2 - Divertissement
  {
    id: 'radio',
    name: 'Radio',
    icon: Radio,
    gradient: 'from-red-400 to-rose-600',
    action: 'scrollTo',
    target: 'detente'
  },
  {
    id: 'weather',
    name: 'Météo',
    icon: Cloud,
    gradient: 'from-sky-400 to-blue-500',
    action: 'scrollTo',
    target: 'detente'
  },
  {
    id: 'tv',
    name: 'TV & Streaming',
    icon: Tv,
    gradient: 'from-indigo-400 to-purple-600',
    action: 'scrollTo',
    target: 'tv'
  },
  {
    id: 'news',
    name: 'Actualités',
    icon: Newspaper,
    gradient: 'from-slate-400 to-gray-600',
    action: 'scrollTo',
    target: 'news'
  },
  {
    id: 'events',
    name: 'Événements',
    icon: Calendar,
    gradient: 'from-teal-400 to-emerald-600',
    action: 'scrollTo',
    target: 'events'
  },
  {
    id: 'promos',
    name: 'Promos',
    icon: Zap,
    gradient: 'from-orange-400 to-red-500',
    action: 'scrollTo',
    target: 'promos'
  },
  // Rangée 3 - Communauté & Stats
  {
    id: 'leaderboard',
    name: 'Classement',
    icon: Trophy,
    gradient: 'from-yellow-400 to-orange-500',
    action: 'scrollTo',
    target: 'leaderboard'
  },
  {
    id: 'polls',
    name: 'Sondages',
    icon: Vote,
    gradient: 'from-fuchsia-400 to-pink-600',
    action: 'scrollTo',
    target: 'polls'
  },
  {
    id: 'stats',
    name: 'Mes Stats',
    icon: BarChart3,
    gradient: 'from-violet-400 to-purple-600',
    action: 'scrollAndOpen',
    target: 'stats'
  },
  {
    id: 'referral',
    name: 'Parrainage',
    icon: Users,
    gradient: 'from-lime-400 to-green-600',
    action: 'scrollTo',
    target: 'referral'
  },
  {
    id: 'profile',
    name: 'Mon Profil',
    icon: User,
    gradient: 'from-cyan-400 to-teal-600',
    action: 'setShow',
    target: 'showProfileEditor'
  },
  {
    id: 'security',
    name: 'Sécurité',
    icon: Shield,
    gradient: 'from-green-400 to-emerald-600',
    action: 'setShow',
    target: 'show2FASetup'
  },
  // Rangée 4 - Outils
  {
    id: 'qrcode',
    name: 'Mon QR',
    icon: QrCode,
    gradient: 'from-gray-400 to-slate-600',
    action: 'scrollTo',
    target: 'qrcode-section'
  },
  {
    id: 'apps',
    name: 'Apps Hub',
    icon: Grid3X3,
    gradient: 'from-blue-400 to-indigo-600',
    action: 'scrollTo',
    target: 'apps'
  },
];

// Composant icône avec style macOS et cadre gris pour visibilité en mode soleil
const QuickAccessIcon = ({ module, onClick, index }) => {
  const IconComponent = module.icon;
  
  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ 
        delay: index * 0.03,
        type: 'spring',
        stiffness: 300,
        damping: 20
      }}
      whileHover={{ scale: 1.08, y: -3 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className="flex flex-col items-center gap-1.5 group cursor-pointer"
      data-testid={`quick-access-${module.id}`}
    >
      {/* Cadre gris pour visibilité en mode soleil */}
      <div 
        className="p-1.5 rounded-2xl"
        style={{
          background: 'rgba(55, 65, 81, 0.5)',
          backdropFilter: 'blur(8px)',
        }}
      >
        {/* Icône avec style macOS */}
        <div
          className={`relative w-12 h-12 sm:w-14 sm:h-14 rounded-[22%] bg-gradient-to-br ${module.gradient} flex items-center justify-center shadow-lg transition-shadow duration-200`}
          style={{
            boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
          }}
        >
          {/* Reflet style iOS/macOS */}
          <div 
            className="absolute inset-0 rounded-[22%] opacity-30"
            style={{
              background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%)',
            }}
          />
          
          {/* Icône */}
          <IconComponent className="w-6 h-6 sm:w-7 sm:h-7 text-white drop-shadow-md relative z-10" />
        </div>
      </div>
      
      {/* Label avec fond semi-transparent pour lisibilité */}
      <span 
        className="text-[10px] sm:text-xs font-semibold text-center max-w-[70px] truncate px-1.5 py-0.5 rounded-md"
        style={{
          background: 'rgba(31, 41, 55, 0.7)',
          color: 'white',
          textShadow: '0 1px 2px rgba(0,0,0,0.5)',
        }}
      >
        {module.name}
      </span>
    </motion.button>
  );
};

// Composant grille d'accès rapide
const QuickAccessGrid = ({ onAction }) => {
  
  const handleModuleClick = (module) => {
    if (module.action === 'setShow') {
      onAction('setShow', module.target);
    } else if (module.action === 'scrollAndOpen') {
      // D'abord ouvrir le panneau puis scroller
      onAction('scrollAndOpen', module.target);
    } else if (module.action === 'scrollTo') {
      // Scroll vers la section
      const sectionMap = {
        'vault': '.secure-vault-section, [data-section="vault"]',
        'contact': '.contact-section, [data-section="contact"]',
        'detente': '.detente-section, [data-section="detente"]',
        'tv': '.tv-section, [data-section="tv"]',
        'news': '.news-section, [data-section="news"]',
        'events': '.events-section, [data-section="events"]',
        'promos': '.promos-section, [data-section="promos"]',
        'leaderboard': '.leaderboard-section, [data-section="leaderboard"]',
        'polls': '.polls-section, [data-section="polls"]',
        'referral': '.referral-section, [data-section="referral"]',
        'qrcode-section': '.qrcode-section, [data-section="qrcode"]',
        'apps': '.apps-section, [data-section="apps"]',
        'stats': '.stats-section, [data-section="stats"]',
      };
      const selector = sectionMap[module.target];
      if (selector) {
        const element = document.querySelector(selector);
        if (element) {
          // Utiliser 'start' pour les sections détente/radio/météo pour un meilleur positionnement
          const blockPosition = module.target === 'detente' ? 'start' : 'center';
          element.scrollIntoView({ behavior: 'smooth', block: blockPosition });
        }
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="w-full max-w-4xl mx-auto px-4 py-6"
    >
      {/* Titre */}
      <div className="text-center mb-5">
        <h3 className="text-lg font-bold text-white flex items-center justify-center gap-2">
          🚀 Accès Rapide
        </h3>
        <p className="text-xs text-gray-400 mt-1">Navigue en un clic vers tous tes modules</p>
      </div>
      
      {/* Grille d'icônes */}
      <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-7 gap-3 sm:gap-4">
        {QUICK_ACCESS_MODULES.map((module, index) => (
          <QuickAccessIcon
            key={module.id}
            module={module}
            index={index}
            onClick={() => handleModuleClick(module)}
          />
        ))}
      </div>
    </motion.div>
  );
};

export default QuickAccessGrid;
